import * as React from "react"
import { cn } from "../../lib/utils"
import { CalendarIcon, X } from "lucide-react"
import { Button } from "./button"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { Label } from "./label"

export interface SimpleDatePickerProps {
  onUpdate?: (values: {
    dateFrom: Date | undefined
    dateTo: Date | undefined
  }) => void
  initialDateFrom?: Date | string
  initialDateTo?: Date | string
  className?: string
}

const SimpleDatePicker = React.forwardRef<HTMLDivElement, SimpleDatePickerProps>(
  ({
    onUpdate,
    initialDateFrom,
    initialDateTo,
    className,
  }, ref) => {
    const [dateFrom, setDateFrom] = React.useState<Date | undefined>(
      initialDateFrom ? new Date(initialDateFrom) : undefined
    )
    const [dateTo, setDateTo] = React.useState<Date | undefined>(
      initialDateTo ? new Date(initialDateTo) : undefined
    )

    // Sync internal state with external props
    React.useEffect(() => {
      setDateFrom(initialDateFrom ? new Date(initialDateFrom) : undefined);
      setDateTo(initialDateTo ? new Date(initialDateTo) : undefined);
    }, [initialDateFrom, initialDateTo]);

    const formatMonthName = (date: Date | undefined) => {
      if (!date) return ""
      const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${month} ${year}`;
    }

    const formatDateWithMonthName = (date: Date | undefined) => {
      if (!date) return ""
      const day = date.getDate().toString().padStart(2, '0');
      const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }

    const getDateRangeText = () => {
      if (!dateFrom && !dateTo) return "Select date range"
      if (dateFrom && !dateTo) return `From ${formatDateWithMonthName(dateFrom)}`
      if (dateFrom && dateTo) {
        return `${formatDateWithMonthName(dateFrom)} - ${formatDateWithMonthName(dateTo)}`
      }
      return "Select date range"
    }

    const handleClear = () => {
      setDateFrom(undefined)
      setDateTo(undefined)
      
      // Notify parent component of the clear action
      if (onUpdate) {
        onUpdate({
          dateFrom: undefined,
          dateTo: undefined,
        })
      }
    }

    const handleDateChange = (field: 'from' | 'to', value: string) => {
      const newDate = value ? new Date(value) : undefined;
      
      if (field === 'from') {
        setDateFrom(newDate);
      } else {
        setDateTo(newDate);
      }

      // Notify parent component of the change
      if (onUpdate) {
        onUpdate({
          dateFrom: field === 'from' ? newDate : dateFrom,
          dateTo: field === 'to' ? newDate : dateTo,
        });
      }
    }

    return (
      <div className={cn("relative", className)} ref={ref}>
        <Popover>
          <PopoverTrigger>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                "h-9 rounded-md border border-[#E2E5EA] bg-white px-3 py-2 text-[13px]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hero-red/30 focus-visible:ring-offset-0",
                "text-text-grey placeholder:text-heritage-grey",
                className
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
              <span className="flex-1 text-left">
                {dateFrom || dateTo ? (
                  <span className="text-gray-900">{getDateRangeText()}</span>
                ) : (
                  <span className="text-gray-500">Select date range</span>
                )}
              </span>
              {dateFrom || dateTo ? (
                <X 
                  className="h-4 w-4 ml-2 hover:text-gray-600 text-gray-400" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                />
              ) : null}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="p-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Date Range</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="date-from" className="text-xs text-gray-600">From</Label>
                      <div className="relative">
                        <input
                          type="date"
                          id="date-from"
                          aria-label="Select start date"
                          title="Select start date"
                          value={dateFrom?.toISOString().split('T')[0] || ''}
                          onChange={(e) => handleDateChange('from', e.target.value)}
                          className={cn(
                            "flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
                            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-200 focus-visible:ring-offset-0",
                            "text-transparent"
                          )}
                        />
                        {dateFrom ? (
                          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-sm text-gray-900">
                            {formatDateWithMonthName(dateFrom)}
                          </div>
                        ) : (
                          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-sm text-gray-400">
                            dd-mm-yyyy
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="date-to" className="text-xs text-gray-600">To</Label>
                      <div className="relative">
                        <input
                          type="date"
                          id="date-to"
                          aria-label="Select end date"
                          title="Select end date"
                          value={dateTo?.toISOString().split('T')[0] || ''}
                          onChange={(e) => handleDateChange('to', e.target.value)}
                          className={cn(
                            "flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
                            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-200 focus-visible:ring-offset-0",
                            "text-transparent"
                          )}
                        />
                        {dateTo ? (
                          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-sm text-gray-900">
                            {formatDateWithMonthName(dateTo)}
                          </div>
                        ) : (
                          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-sm text-gray-400">
                            dd-mm-yyyy
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClear}
                    className="text-xs"
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    )
  }
)

SimpleDatePicker.displayName = "SimpleDatePicker"

export { SimpleDatePicker }

